import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../store/AuthContext';
import { RefreshCw, AlertTriangle, Clock, FileX, CheckCircle, Eye, CheckSquare, Users, XCircle } from 'lucide-react';

interface AlertRow {
  id: string;
  facility_id: string;
  resident_id: string;
  type: 'inactive' | 'missed' | 'no_notes';
  status: string;
  reason_text: string;
  triggered_at: string;
  residentName: string;
  roomNumber: string | null;
}

const statusConfig: Record<string, { label: string; bg: string; text: string; icon: any }> = {
  inactive: { label: 'Inactive', bg: 'bg-red-900/30', text: 'text-red-400', icon: AlertTriangle },
  missed: { label: 'Missed Activity', bg: 'bg-orange-900/30', text: 'text-orange-400', icon: Clock },
  no_notes: { label: 'No Notes', bg: 'bg-yellow-900/30', text: 'text-yellow-400', icon: FileX },
};

const getUrgency = (triggeredAt: string): 'new' | 'overdue' | 'normal' => {
  const diffMs = Date.now() - new Date(triggeredAt).getTime();
  const diffHours = diffMs / (1000 * 60 * 60);
  if (diffHours <= 1) return 'new';
  if (diffHours > 2) return 'overdue';
  return 'normal';
};

export function CareFollowThroughPage() {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [loading, setLoading] = useState(true);

  const loadAlerts = useCallback(async () => {
    if (!user) return;
    if (!supabase) return;
    setLoading(true);
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user.id)
        .single();
      if (!profile?.tenant_id) return;
      const { data: alertRows } = await supabase
        .from('alerts')
        .select('*, residents(full_name, room_number)')
        .eq('facility_id', profile.tenant_id)
        .eq('status', 'open')
        .order('triggered_at', { ascending: false });
      const mapped: AlertRow[] = (alertRows || []).map((a: any) => ({
        id: a.id,
        facility_id: a.facility_id,
        resident_id: a.resident_id,
        type: a.type,
        status: a.status,
        reason_text: a.reason_text,
        triggered_at: a.triggered_at,
        residentName: a.residents?.full_name || 'Unknown',
        roomNumber: a.residents?.room_number || null,
      }));
      setAlerts(mapped);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadAlerts(); }, [loadAlerts]);

  const markReviewed = async (alertId: string) => {
    if (!supabase) return;
    await supabase.from('alerts').update({ status: 'acknowledged', assigned_to: user?.id }).eq('id', alertId);
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const resolveAlert = async (alertId: string) => {
    if (!supabase) return;
    await supabase.from('alerts').update({ status: 'resolved', assigned_to: user?.id, resolved_at: new Date().toISOString() }).eq('id', alertId);
    setAlerts(prev => prev.filter(a => a.id !== alertId));
  };

  const formatTime = (ts: string) => new Date(ts).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  const inactiveCount = alerts.filter(a => a.type === 'inactive').length;
  const missedCount = alerts.filter(a => a.type === 'missed').length;
  const noNotesCount = alerts.length;

  if (loading) return (<main className="p-6"><div className="text-center text-[rgb(var(--admin-text-muted))]">Loading alerts...</div></main>);

  return (
    <main className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[rgb(var(--admin-text-primary))]">Care Follow-Through</h1>
          <p className="text-sm text-[rgb(var(--admin-text-muted))]">Actionable queue for staff oversight</p>
        </div>
        <button onClick={loadAlerts} className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[rgb(var(--admin-surface-2))] text-[rgb(var(--admin-text-secondary))] hover:text-[rgb(var(--admin-text-primary))] transition-colors duration-200">
          <RefreshCw size={16} /> Refresh Queue
        </button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="rounded-3xl bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-red-900/30 flex items-center justify-center"><Users size={20} className="text-red-400" /></div>
          <div><div className="text-2xl font-semibold text-[rgb(var(--admin-text-primary))]">{inactiveCount}</div><div className="text-xs text-[rgb(var(--admin-text-muted))]">Inactive Seniors</div></div>
        </div>
        <div className="rounded-3xl bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-orange-900/30 flex items-center justify-center"><Clock size={20} className="text-orange-400" /></div>
          <div><div className="text-2xl font-semibold text-[rgb(var(--admin-text-primary))]">{missedCount}</div><div className="text-xs text-[rgb(var(--admin-text-muted))]">Missed Activities</div></div>
        </div>
        <div className="rounded-3xl bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm p-5 flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-orange-900/30 flex items-center justify-center"><FileX size={20} className="text-orange-600" /></div>
          <div><div className="text-2xl font-semibold text-[rgb(var(--admin-text-primary))]">{noNotesCount}</div><div className="text-xs text-[rgb(var(--admin-text-muted))]">Needs Follow-Up</div></div>
        </div>
      </div>
      <div className="rounded-3xl bg-[rgb(var(--admin-surface))] border border-[rgb(var(--admin-border))] shadow-warm">
        <div className="p-5 border-b border-[rgb(var(--admin-border))]">
          <h2 className="text-base font-semibold text-[rgb(var(--admin-text-primary))]">Follow-Up Queue</h2>
          <p className="text-xs text-[rgb(var(--admin-text-muted))] mt-1">{alerts.length} resident{alerts.length !== 1 ? 's' : ''} need attention</p>
        </div>
        {alerts.length === 0 ? (
          <div className="p-10 text-center">
            <CheckCircle size={32} className="mx-auto mb-3 text-[rgb(var(--admin-text-muted))]" />
            <p className="text-sm text-[rgb(var(--admin-text-muted))]">All residents are on track</p>
          </div>
        ) : (
          <div className="divide-y divide-[rgb(var(--admin-border))]">
            {alerts.map(item => {
              const config = statusConfig[item.type];
              const StatusIcon = config.icon;
              const urgency = getUrgency(item.triggered_at);
              const dotColor = urgency === 'new' ? 'bg-amber-400' : urgency === 'overdue' ? 'bg-red-400' : 'bg-gray-500/30';
              return (
                <div key={item.id} className="flex items-center justify-between px-5 py-4 hover:bg-[rgb(var(--admin-surface-2))] transition-colors duration-200">
                  <div className="flex items-center gap-4">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${config.bg}`}><StatusIcon size={16} className={config.text} /></div>
                    <div>
                      <div className="text-sm font-medium text-[rgb(var(--admin-text-primary))] flex items-center gap-1.5">
                        <span className={`inline-block w-2 h-2 rounded-full ${dotColor}`} />
                        {item.residentName}
                        {item.roomNumber && <span className="ml-2 text-xs text-[rgb(var(--admin-text-muted))]">Room {item.roomNumber}</span>}
                      </div>
                      <div className="text-xs text-[rgb(var(--admin-text-muted))] mt-0.5">{item.reason_text}</div>
                    </div>
                  </div>
                                    <div className="flex flex-col items-end gap-2">
                    <div className="flex items-center gap-3">
                    <span className={`text-xs px-2 py-1 rounded-lg min-w-[80px] text-center inline-block ${config.bg} ${config.text}`}>{config.label}</span>
                      <span className="text-xs text-[rgb(var(--admin-text-muted))]">{formatTime(item.triggered_at)}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <a href={`#/admin/residents/${item.resident_id}`} className="flex items-center gap-1 text-xs text-[rgb(var(--admin-text-secondary))] hover:text-[rgb(var(--admin-text-primary))] transition-colors"><Eye size={14} /> View Resident</a>
                      <button onClick={() => markReviewed(item.id)} className="flex items-center gap-1 text-xs text-[rgb(var(--admin-text-secondary))] hover:text-[rgb(var(--admin-text-primary))] transition-colors"><CheckSquare size={14} /> Mark Reviewed</button>
                      <button onClick={() => resolveAlert(item.id)} className="flex items-center gap-1 text-xs text-[rgb(var(--admin-text-secondary))] hover:text-[rgb(var(--admin-text-primary))] transition-colors"><XCircle size={14} /> Resolve</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}